import { motion } from 'framer-motion';
import {
  FileText,
  IndianRupee,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import Toggle from '@ui/Toggle';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import { formatIndianNumberShort } from '../../utils/numberFormatter';

export const ProfitLossCalculator = () => {
  // Profit/Loss Calculator State
  const [investmentType, setInvestmentType] = useState<
    'stocks' | 'mutual_funds'
  >('stocks');
  const [buyPrice, setBuyPrice] = useState<number | null>(100);
  const [sellPrice, setSellPrice] = useState<number | null>(120);
  const [quantity, setQuantity] = useState<number | null>(100);
  const [showCharges, setShowCharges] = useState<boolean>(false);
  const [brokerage, setBrokerage] = useState<number | null>(0.5);
  const [stt, setStt] = useState<number | null>(0.1);
  const [otherCharges, setOtherCharges] = useState<number | null>(50);

  const [results, setResults] = useState({
    totalInvestment: 0,
    totalSaleValue: 0,
    grossProfit: 0,
    totalCharges: 0,
    netProfit: 0,
    profitPercentage: 0,
    isProfit: true,
  });

  // Calculate Profit/Loss
  const calculateProfitLoss = useCallback(() => {
    // Handle null values by using defaults
    const buyPriceValue = buyPrice || 0;
    const sellPriceValue = sellPrice || 0;
    const quantityValue = quantity || 0;

    // Use custom charges if toggle is on, otherwise no charges
    const brokerageValue = showCharges ? brokerage || 0 : 0;
    const sttValue = showCharges ? stt || 0 : 0;
    const otherChargesValue = showCharges ? otherCharges || 0 : 0;

    const totalInvestment = buyPriceValue * quantityValue;
    const totalSaleValue = sellPriceValue * quantityValue;
    const grossProfit = totalSaleValue - totalInvestment;

    // Calculate charges
    let buyBrokerage = 0;
    let sellBrokerage = 0;
    let sttCharges = 0;

    if (investmentType === 'stocks') {
      // For stocks, brokerage is typically on both buy and sell
      buyBrokerage = (totalInvestment * brokerageValue) / 100;
      sellBrokerage = (totalSaleValue * brokerageValue) / 100;
      // STT is typically on sell side for delivery trades
      sttCharges = (totalSaleValue * sttValue) / 100;
    } else {
      // For mutual funds, typically exit load and other charges
      sellBrokerage = (totalSaleValue * brokerageValue) / 100;
      sttCharges = (totalSaleValue * sttValue) / 100;
    }

    const totalCharges =
      buyBrokerage + sellBrokerage + sttCharges + otherChargesValue;
    const netProfit = grossProfit - totalCharges;
    const profitPercentage =
      totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;
    const isProfit = netProfit >= 0;

    setResults({
      totalInvestment,
      totalSaleValue,
      grossProfit,
      totalCharges,
      netProfit,
      profitPercentage,
      isProfit,
    });
  }, [
    buyPrice,
    sellPrice,
    quantity,
    showCharges,
    brokerage,
    stt,
    otherCharges,
    investmentType,
  ]);

  // Recalculate when inputs change
  useEffect(() => {
    calculateProfitLoss();
  }, [calculateProfitLoss]);

  const investmentTypeOptions = [
    { value: 'stocks', label: 'Stocks/equity' },
    { value: 'mutual_funds', label: 'Mutual funds' },
  ];

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  return (
    <ToolLayout
      title={`Profit/loss calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="Profit/loss calculator"
      description="Calculate your profit or loss from stock and mutual fund investments with detailed breakdown of charges and taxes"
      keywords="profit loss calculator, stock profit calculator, mutual fund returns, investment calculator, trading profit"
      canonicalUrl="https://niveshright.com/tools/profit-loss-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.sage}20` }}
        >
          <TrendingUp size={32} style={{ color: COLORS.sage }} />
        </div>
      }
      showBreadcrumb={true}
      cta={
        <WhatsAppCTA
          title="Need investment advice?"
          description="Get personalized guidance to optimize your investment strategy and improve your returns."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your profit/loss calculator with these details:

INVESTMENT TYPE: ${investmentType.charAt(0).toUpperCase() + investmentType.slice(1)}

INVESTMENT SUMMARY:
• Total investment: ₹${results.totalInvestment.toLocaleString('en-IN')}
• Sale value: ₹${results.totalSaleValue.toLocaleString('en-IN')}
• Total charges: ₹${results.totalCharges.toLocaleString('en-IN')}

PROFIT/LOSS ANALYSIS:
• ${results.isProfit ? 'Net profit' : 'Net loss'}: ₹${Math.abs(results.netProfit).toLocaleString('en-IN')}
• Return percentage: ${formatPercentage(results.profitPercentage)}
• Gross ${results.grossProfit >= 0 ? 'profit' : 'loss'}: ₹${Math.abs(results.grossProfit).toLocaleString('en-IN')}

${
  results.isProfit
    ? `Great! I made a profit of ${Math.abs(results.profitPercentage)}%. I'd like to discuss strategies to optimize my gains and reinvest wisely.`
    : `I incurred a loss of ${Math.abs(results.profitPercentage)}%. I need advice on minimizing future losses and better investment strategies.`
}

Can you help me with personalized investment guidance?`}
          buttonText="Get expert advice"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.sage}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Investment details
          </h2>

          {/* Investment Type */}
          <RadioGroup
            label="Investment type"
            options={investmentTypeOptions}
            value={investmentType}
            onChange={value =>
              setInvestmentType(value as 'stocks' | 'mutual_funds')
            }
            className="mb-8"
          />

          {/* Buy Price */}
          <NumberInputWithSlider
            label={
              investmentType === 'stocks' ? 'Buy price per share' : 'Buy NAV'
            }
            value={buyPrice}
            onChange={setBuyPrice}
            min={1}
            max={1000000000}
            step={0.1}
            prefix="₹"
            showTooltip={true}
            minLabel="₹1"
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          {/* Sell Price */}
          <NumberInputWithSlider
            label={
              investmentType === 'stocks' ? 'Sell price per share' : 'Sell NAV'
            }
            value={sellPrice}
            onChange={setSellPrice}
            min={1}
            max={1000000000}
            step={0.1}
            prefix="₹"
            showTooltip={true}
            minLabel="₹1"
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          {/* Quantity */}
          <NumberInputWithSlider
            label={
              investmentType === 'stocks'
                ? 'Number of shares'
                : 'Number of units'
            }
            value={quantity}
            onChange={setQuantity}
            min={1}
            max={1000000000}
            step={1}
            minLabel="1"
            maxLabel={formatIndianNumberShort(1000000000, false)}
            className="mb-8"
          />

          {/* Charges & Taxes Toggle */}
          <div className="border-t border-gray-200 pt-8 mt-8">
            <div className="flex justify-between mb-2">
              <div>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: COLORS.primary }}
                >
                  Charges & taxes
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Customize your charges and taxes
                </p>
              </div>
              <Toggle
                label=""
                checked={showCharges}
                onChange={setShowCharges}
              />
            </div>

            {showCharges && (
              <div className="space-y-6">
                {/* Brokerage */}
                <NumberInputWithSlider
                  label={
                    investmentType === 'stocks'
                      ? 'Brokerage (%)'
                      : 'Exit load (%)'
                  }
                  value={brokerage}
                  onChange={setBrokerage}
                  min={0}
                  max={5}
                  step={0.01}
                  minLabel="0%"
                  maxLabel="5%"
                  className="mb-8"
                />

                {/* STT */}
                <NumberInputWithSlider
                  label={
                    investmentType === 'stocks' ? 'STT (%)' : 'Tax rate (%)'
                  }
                  value={stt}
                  onChange={setStt}
                  min={0}
                  max={1}
                  step={0.001}
                  minLabel="0%"
                  maxLabel="1%"
                  className="mb-8"
                />

                {/* Other Charges */}
                <NumberInputWithSlider
                  label="Other charges"
                  value={otherCharges}
                  onChange={setOtherCharges}
                  min={0}
                  max={1000}
                  step={1}
                  prefix="₹"
                  showTooltip={true}
                  minLabel="₹0"
                  maxLabel={`₹${formatIndianNumberShort(1000, false)}`}
                  className="mb-8"
                />
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Investment amount"
              value={<LocalizedCurrency value={results.totalInvestment} />}
              subtitle="Total amount invested"
              icon={IndianRupee}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.6}
            />

            <ResultSummaryCard
              title="Sale value"
              value={<LocalizedCurrency value={results.totalSaleValue} />}
              subtitle="Gross sale proceeds"
              icon={Target}
              iconColor={COLORS.sage}
              valueColor={COLORS.sage}
              delay={0.7}
            />

            <ResultSummaryCard
              title={results.isProfit ? 'Net profit' : 'Net loss'}
              value={<LocalizedCurrency value={Math.abs(results.netProfit)} />}
              subtitle={`${formatPercentage(results.profitPercentage)} returns`}
              icon={results.isProfit ? TrendingUp : TrendingDown}
              iconColor={results.isProfit ? COLORS.sage : '#ef4444'}
              valueColor={results.isProfit ? COLORS.sage : '#ef4444'}
              delay={0.8}
            />
          </div>

          {/* Transaction Breakdown */}
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <div className="flex items-center mb-4">
              <FileText
                size={20}
                style={{ color: COLORS.sage }}
                className="mr-2"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Transaction breakdown
              </h3>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Investment</span>
                <span className="text-sm text-gray-500">
                  <LocalizedCurrency value={results.totalInvestment} />
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Sale value</span>
                <span
                  className="text-sm font-medium"
                  style={{
                    color: results.grossProfit >= 0 ? COLORS.sage : '#ef4444',
                  }}
                >
                  <LocalizedCurrency value={results.totalSaleValue} /> (
                  {results.grossProfit >= 0 ? '+' : ''}
                  {(
                    (results.grossProfit / results.totalInvestment) *
                    100
                  ).toFixed(2)}
                  %)
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Charges & taxes</span>
                <span className="text-sm font-medium text-red-600">
                  <LocalizedCurrency value={results.totalCharges} /> (-
                  {(
                    (results.totalCharges / results.totalInvestment) *
                    100
                  ).toFixed(2)}
                  %)
                </span>
              </div>
              <div className="flex justify-between items-center py-3 bg-gray-50 rounded-lg px-3">
                <span
                  className="text-sm font-semibold"
                  style={{ color: COLORS.primary }}
                >
                  Net {results.isProfit ? 'profit' : 'loss'}
                </span>
                <span
                  className="text-lg font-bold"
                  style={{ color: results.isProfit ? COLORS.sage : '#ef4444' }}
                >
                  <LocalizedCurrency value={results.netProfit} /> (
                  {formatPercentage(results.profitPercentage)})
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </ToolLayout>
  );
};
